/**
 * AIAnalysisChat — shared, ticker-aware AI chat component.
 *
 * Used by:
 *   • Stock Analysis (Dashboard.jsx)   — mode="stock", ticker=<symbol>
 *   • Sector Analysis (SectorAnalysisPage.jsx) — mode="sector"
 *
 * On mount it POSTs the current ticker + numbers to
 * /api/sector-analysis/suggested-questions so the chat opens with 4 short,
 * data-aware starter questions ("SMH down 3% 1D but +27% 50D — crash or
 * profit-taking?" etc).  Clicking a suggestion sends it as the user's
 * first message.
 *
 * Actual chat is powered by /api/sector-analysis/chat which returns JSON
 * with answer / recommendation / citations / suggested_stocks /
 * alternative_etfs / optional proposed_allocation.  When an allocation is
 * proposed we auto-fetch /api/sector-analysis/allocation-curve and render
 * the equity-curve vs SPY.
 *
 * Session memory is per-mount (session_id randomised) — clears on
 * refresh, per user product decision.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  RefreshCw, Send, ExternalLink, Bot, Zap, LineChart as LineChartIcon, Coins,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { API } from "../config/api";

export default function AIAnalysisChat({
  mode = "stock",        // "stock" | "sector"
  ticker,                // required for "stock" mode
  tickerName,            // display name (optional)
  tickerType,            // "ETF" | "Stock" (optional, used in prompts)
  returns,               // {r1d,r5d,r10d,...} — for suggested questions
  ew,                    // Early Warnings scores (optional)
  extraContext = {},     // extra JSON blob passed to /chat's `context`
  title,                 // card title override
}) {
  const [sessionId] = useState(() =>
    `${mode}-${(ticker || "sec").toLowerCase()}-${Math.random().toString(36).slice(2, 10)}`
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [sending, setSending]   = useState(false);
  const [err, setErr]           = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [catalystSummary, setCatalystSummary] = useState(null);
  const [actionSuggestion, setActionSuggestion] = useState(null);
  const [suggBusy, setSuggBusy]       = useState(false);
  const [suggErr, setSuggErr]         = useState(null);
  const [suggReloadKey, setSuggReloadKey] = useState(0);
  const [lastFailedMsg, setLastFailedMsg] = useState(null);
  // Market brief is on-demand only (user directive Sep 11 2026: no auto LLM call on page load)
  const [briefRequested, setBriefRequested] = useState(mode !== "market");
  const [briefMeta, setBriefMeta] = useState(null);   // {usage, news_sources, generated_at, cached, ai_source}
  const [newsSources, setNewsSources] = useState([]);
  const listRef = useRef(null);
  const sessionUsage = useMemo(() => {
    const items = [...messages.filter(m => m.role === "assistant" && m.usage).map(m => m.usage),
                   ...(briefMeta?.usage && !briefMeta.cached ? [briefMeta.usage] : [])];
    return items.reduce((a, u) => ({ tokens: a.tokens + (u.total_tokens || 0), cost: a.cost + (u.est_cost_usd || 0), calls: a.calls + 1 }),
                        { tokens: 0, cost: 0, calls: 0 });
  }, [messages, briefMeta]);

  // Auto-scan the ticker's / market's data → 4 starter questions.
  // Serialize returns/ew so effect only re-runs when their VALUES change
  // (parent re-renders create new object refs every tick otherwise).
  // Auto-retries up to 3 attempts with exponential backoff so a slow /
  // rate-limited LLM call doesn't leave the suggestions spinner stuck.
  const returnsKey = JSON.stringify(returns || {});
  const ewKey      = JSON.stringify(ew || {});
  const extraKey   = JSON.stringify(extraContext || {});
  useEffect(() => {
    if (mode === "sector") { setSuggestions([]); setSuggBusy(false); setSuggErr(null); return; }
    if (mode === "stock" && !ticker) { setSuggestions([]); setSuggBusy(false); setSuggErr(null); return; }
    if (!briefRequested) { setSuggBusy(false); return; }
    let cancelled = false;
    setSuggBusy(true); setSuggestions([]); setSuggErr(null);
    const payloadType =
      mode === "market"          ? "market" :
      mode === "portfolio"       ? "portfolio" :
      mode === "universe-stocks" ? "universe-stocks" :
      mode === "universe-etfs"   ? "universe-etfs" :
      (tickerType || "Stock").toLowerCase() === "etf" ? "etf" : "stock";

    const attempt = async (n) => {
      try {
        const r = await axios.post(`${API}/sector-analysis/suggested-questions`, {
          symbol:  ticker || (mode === "universe-stocks" ? "UNIVERSE_STOCKS" : mode === "universe-etfs" ? "UNIVERSE_ETFS" : mode === "portfolio" ? "PORTFOLIO" : "MARKET"),
          name:    tickerName || (
                     mode === "market"          ? "US Market" :
                     mode === "portfolio"       ? "Generated Portfolio" :
                     mode === "universe-stocks" ? "All Stocks Universe" :
                     mode === "universe-etfs"   ? "All ETFs Universe" :
                     ticker),
          type:    payloadType,
          returns: returns || {},
          ew:      ew || {},
          extra:   extraContext || {},
        }, { timeout: 30000 });
        if (cancelled) return;
        const qs = r.data?.questions || [];
        setSuggestions(qs);
        setCatalystSummary(r.data?.catalyst_summary || null);
        setActionSuggestion(r.data?.action_suggestion || null);
        setNewsSources(r.data?.news_sources || []);
        setBriefMeta({ usage: r.data?.usage || null, cached: !!r.data?.cached, generated_at: r.data?.generated_at,
                       ai_source: r.data?.ai_source, cache_age: r.data?.cache_age_minutes });
        setSuggErr(qs.length === 0 && !r.data?.catalyst_summary && !r.data?.action_suggestion
          ? "No suggestions returned — try refresh" : null);
        setSuggBusy(false);
      } catch (e) {
        if (cancelled) return;
        if (n < 3) {
          await new Promise(res => setTimeout(res, 1500 * (2 ** (n - 1))));
          if (!cancelled) attempt(n + 1);
        } else {
          setSuggErr("Failed to load suggestions — click refresh to retry");
          setSuggBusy(false);
        }
      }
    };
    // Debounced: page data streams in over several seconds — wait until the
    // context settles (2.5s with no changes) then fetch ONCE with full data,
    // instead of churning a new LLM call on every partial update.
    const debounce = setTimeout(() => attempt(1), 2500);
    return () => { cancelled = true; clearTimeout(debounce); };
  }, [mode, ticker, tickerName, tickerType, returnsKey, ewKey, extraKey, suggReloadKey, briefRequested]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const buildContext = useMemo(() => () => {
    // Trim to keep the LLM prompt fast + on-topic
    return {
      mode,
      ticker: ticker || null,
      ticker_name: tickerName || null,
      ticker_type: tickerType || null,
      returns: returns || {},
      ew:      ew || {},
      ...extraContext,
    };
  }, [mode, ticker, tickerName, tickerType, returnsKey, ewKey,
      JSON.stringify(extraContext || {})]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput(""); setErr(null); setLastFailedMsg(null);
    setMessages(m => [...m, { role: "user", text: msg }]);
    setSending(true);

    // Auto-retry: transient Cloudflare 502/520/524 and LLM 504s
    // shouldn't leave the chat stuck on an error.
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const r = await axios.post(`${API}/sector-analysis/chat`, {
          message: msg, session_id: sessionId, context: buildContext(),
        }, { timeout: 90000 });
        const reply = r.data;
        // Streaming reveal: push the assistant message with `streaming=true`
        // and let ChatMessage typewriter it in over ~1-3s (feels 3-4×
        // faster than showing the full block at once, per user product
        // decision — total network+LLM time is unchanged).
        setMessages(m => [...m, { role: "assistant", ...reply, streaming: true }]);
        if (Array.isArray(reply.proposed_allocation) && reply.proposed_allocation.length > 0) {
          try {
            const c = await axios.post(`${API}/sector-analysis/allocation-curve`, {
              allocations: reply.proposed_allocation.map(a => ({
                ticker: a.ticker, weight_pct: a.weight_pct,
              })),
            }, { timeout: 30000 });
            setMessages(m => m.map((mm, i) => i === m.length - 1 ? { ...mm, curve: c.data } : mm));
          } catch (_) { /* silent */ }
        }
        setSending(false);
        return;
      } catch (e) {
        lastError = e;
        const status = e?.response?.status;
        const transient = !status || status === 502 || status === 504 || status === 520 || status === 521 || status === 522 || status === 524;
        if (attempt < 3 && transient) {
          await new Promise(res => setTimeout(res, 2000 * attempt));
          continue;
        }
        break;
      }
    }
    const status = lastError?.response?.status;
    const rawDetail = lastError?.response?.data?.detail || lastError?.response?.data;
    // If the response body is HTML (e.g. Cloudflare's 524 error page),
    // it may leak through as a string.  Detect and replace with a clean
    // message so we never render raw HTML in the chat error UI.
    const looksLikeHtml = typeof rawDetail === "string"
      && (rawDetail.trim().startsWith("<") || /Cloudflare|origin web server/i.test(rawDetail));
    const errMsg = (status === 502 || status === 520 || status === 521 || status === 522 || status === 524 || looksLikeHtml)
      ? "The AI is taking longer than usual. Click Retry — it usually responds on the second attempt."
      : (typeof rawDetail === "string" && !looksLikeHtml ? rawDetail : null)
        || lastError?.message
        || "Chat failed";
    setErr(errMsg);
    setLastFailedMsg(msg);
    setSending(false);
  };

  const refreshEverything = () => {
    setErr(null);
    if (mode === "market" && !briefRequested) { setBriefRequested(true); return; }
    setSuggReloadKey(k => k + 1);
    if (lastFailedMsg) {
      const m = lastFailedMsg;
      setLastFailedMsg(null);
      // Pop the last unanswered user message off, then re-send
      setMessages(prev => {
        const idx = [...prev].reverse().findIndex(x => x.role === "user" && x.text === m);
        if (idx < 0) return prev;
        return prev.slice(0, prev.length - 1 - idx);
      });
      // Re-send after state settles
      setTimeout(() => sendMessage(m), 100);
    }
  };

  const heading = title || (
    mode === "stock"           ? `${ticker || ""} AI Chat` :
    mode === "market"          ? "Market AI Chat" :
    mode === "portfolio"       ? "Portfolio Sanity Check — AI Chat" :
    mode === "universe-stocks" ? "All Stocks — AI Chat" :
    mode === "universe-etfs"   ? "All ETFs — AI Chat" :
                                 "Sector AI Chat"
  );
  const placeholder =
    mode === "stock"           ? `Ask anything about ${ticker || "this ticker"} — crashes, rotations, consolidation, buy/hold/sell…` :
    mode === "market"          ? "Ask about market direction, sector rotations, macro impacts, or crash risk…" :
    mode === "portfolio"       ? "Sanity-check the generated portfolio — bad picks, concentration, drawdown fit…" :
    mode === "universe-stocks" ? "Ask about breakouts, buy/sell/hold, deteriorating or recovering stocks…" :
    mode === "universe-etfs"   ? "Ask about breakout ETFs, buy/sell/hold, or top-performing categories…" :
                                 "Ask about a sector, subsector, or ask for an allocation…";

  return (
    <Card className="border-purple-900/60 bg-slate-900/70 h-[1050px] flex flex-col" data-testid="ai-chat-card">
      <CardHeader className="pb-2 shrink-0">
        <CardTitle className="text-base sm:text-lg text-white flex items-center gap-2 flex-wrap">
          <Bot className="w-5 h-5 text-purple-400" />
          {heading}
          <span className="text-xs text-slate-400 font-normal ml-auto">
            Session-only memory · Claude Sonnet 4.5 · SEC / Bloomberg / Reuters / Scholar citations
          </span>
          <span className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-950/60 px-2 py-0.5 text-[10px] font-mono text-slate-300"
                title="Tokens billed this session (brief + chat answers). Cost is an estimate at published list prices."
                data-testid="ai-chat-session-cost">
            <Coins className="w-3 h-3 text-amber-400" />
            session: {sessionUsage.tokens.toLocaleString()} tok · ${sessionUsage.cost.toFixed(4)}
          </span>
          <button
            type="button"
            onClick={refreshEverything}
            disabled={sending || suggBusy}
            data-testid="ai-chat-refresh"
            title="Refresh suggestions and retry any failed message"
            className="inline-flex items-center gap-1.5 rounded border border-slate-600 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-100 hover:bg-slate-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${(sending || suggBusy) ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </CardTitle>
      </CardHeader>
      {/* Fixed-height frame — the card never grows/shrinks, so page content
          below stays put; everything inside scrolls instead. */}
      <CardContent className="flex-1 flex flex-col min-h-0 space-y-3 overflow-hidden">
        <div ref={listRef}
             className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1"
             data-testid="ai-chat-list">
          {messages.length === 0 && (
            <SuggestionPanel
              mode={mode}
              ticker={ticker}
              suggestions={suggestions}
              suggBusy={suggBusy}
              suggErr={suggErr}
              sending={sending}
              onPick={sendMessage}
              onRetry={() => setSuggReloadKey(k => k + 1)}
              variant="empty-state"
              catalystSummary={catalystSummary}
              actionSuggestion={actionSuggestion}
              briefRequested={briefRequested}
              onRequestBrief={() => setBriefRequested(true)}
              briefMeta={briefMeta}
              newsSources={newsSources}
            />
          )}
          {messages.map((m, i) => <ChatMessage key={i} m={m} />)}
          {sending && (
            <ThinkingIndicator />
          )}
        </div>

        {/* Persistent suggestions strip — keeps the auto-generated
            questions visible AFTER the user picks one, so the other
            questions are still worth reading and can be sent later.
            Only rendered once at least one message is in the chat
            (otherwise the empty-state block already shows them). */}
        {messages.length > 0 && (
          <div className="shrink-0 max-h-[260px] overflow-y-auto">
            <SuggestionPanel
              mode={mode}
              ticker={ticker}
              suggestions={suggestions}
              suggBusy={suggBusy}
              suggErr={suggErr}
              sending={sending}
              onPick={sendMessage}
              onRetry={() => setSuggReloadKey(k => k + 1)}
              variant="persistent"
              catalystSummary={catalystSummary}
              actionSuggestion={actionSuggestion}
              briefRequested={briefRequested}
              onRequestBrief={() => setBriefRequested(true)}
              briefMeta={briefMeta}
              newsSources={newsSources}
            />
          </div>
        )}
        {err && (
          <div className="shrink-0 text-sm text-rose-300 bg-rose-950/40 border border-rose-700 rounded p-3 flex flex-col sm:flex-row sm:items-center gap-2"
               data-testid="ai-chat-error">
            <span className="flex-1">{err}</span>
            <button
              type="button"
              onClick={refreshEverything}
              disabled={sending}
              data-testid="ai-chat-error-retry"
              className="inline-flex items-center gap-1.5 rounded border border-rose-500 bg-rose-800/60 px-3 py-1.5 text-xs font-semibold text-rose-50 hover:bg-rose-700 hover:text-white disabled:opacity-50 transition-colors self-start sm:self-auto"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${sending ? "animate-spin" : ""}`} />
              Retry {lastFailedMsg ? "message" : "now"}
            </button>
          </div>
        )}
        <div className="flex gap-2 shrink-0">
          <input
            data-testid="ai-chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder={placeholder}
            disabled={sending}
            className="flex-1 rounded border border-slate-700 bg-slate-950 px-3 py-2.5 text-base text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
          <button
            type="button"
            onClick={() => sendMessage()}
            disabled={sending || !input.trim()}
            data-testid="ai-chat-send"
            className="inline-flex items-center gap-2 rounded border border-purple-700 bg-purple-800/60 px-5 py-2.5 text-sm font-semibold text-purple-50 hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Suggested-questions panel (reused in empty state + persistent bar) ─ */
function SuggestionPanel({
  mode, ticker, suggestions, suggBusy, suggErr,
  sending, onPick, onRetry, variant, catalystSummary, actionSuggestion,
  briefRequested = true, onRequestBrief, briefMeta, newsSources = [],
}) {
  const isPersistent = variant === "persistent";
  const heading = mode === "stock"
    ? `Suggested questions for ${ticker || "this ticker"}`
    : mode === "market"
      ? "AI market brief"
      : mode === "portfolio"
        ? "Suggested sanity checks for this portfolio"
        : mode === "universe-stocks"
          ? "Suggested questions about the stocks universe"
          : mode === "universe-etfs"
            ? "Suggested questions about the ETFs universe"
            : "Try asking";

  const outerCls = isPersistent
    ? "border-t border-slate-800 pt-3"
    : "text-sm text-slate-300 border border-dashed border-slate-700 rounded p-4";

  return (
    <div className={outerCls} data-testid={`ai-chat-suggestions-${variant}`}>
      <div className="mb-2 font-semibold text-slate-200 flex items-center gap-2 text-sm">
        <Bot className="w-4 h-4 text-purple-400" />
        {heading}:
        {suggBusy && <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-400" />}
      </div>

      {/* Static sector-mode canned prompts when we haven't fetched any */}
      {suggestions.length === 0 && !suggBusy && mode === "sector" && (
        <div className="flex flex-col gap-1.5">
          {[
            "SMH has been down 1D/5D/10D/20D but up 27% over 50D — crash or profit-taking?",
            "Allocate $10,000 across the strongest 4 tech subsector ETFs by momentum.",
            "Which sector is best positioned for the next 3 months and why?",
          ].map((q, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPick(q)}
              disabled={sending}
              data-testid={`ai-chat-suggest-canned-${i}`}
              className="text-left text-sm text-cyan-200 hover:text-cyan-100 hover:bg-slate-800/60 border border-slate-700 rounded px-3 py-2 transition-colors disabled:opacity-50"
            >
              &ldquo;{q}&rdquo;
            </button>
          ))}
        </div>
      )}

      {/* On-demand loader (market mode): no LLM call until the user asks */}
      {mode === "market" && !briefRequested && (
        <div className="border border-dashed border-purple-800/70 bg-purple-950/20 rounded-md px-3 py-3 mb-2 text-xs text-slate-300 flex flex-col sm:flex-row sm:items-center gap-2"
             data-testid="ai-chat-brief-gate">
          <span className="flex-1">The AI market brief (today's catalysts with cited, time-stamped news + a suggested action) is generated only when you ask, to keep token costs down. Typical cost ≈ $0.02–0.04 per brief.</span>
          <button type="button" onClick={onRequestBrief} disabled={sending}
            data-testid="ai-chat-load-brief"
            className="inline-flex items-center gap-1.5 rounded border border-purple-600 bg-purple-800/60 px-3 py-1.5 text-xs font-semibold text-purple-50 hover:bg-purple-700 disabled:opacity-50 self-start sm:self-auto">
            <Bot className="w-3.5 h-3.5" /> Load AI market brief
          </button>
        </div>
      )}

      {/* Today's market catalysts brief (Overview page) — inline time-stamped news citations */}
      {catalystSummary && (
        <div className="border border-amber-900/60 bg-amber-950/20 rounded-md px-3 py-2 mb-2 text-xs leading-relaxed text-amber-100"
             data-testid="ai-chat-catalyst-summary">
          <span className="text-[9px] uppercase tracking-wider text-amber-400 font-bold mr-2">Today's Catalysts</span>
          {renderWithLinks(catalystSummary)}
          {newsSources.length > 0 && (
            <details className="mt-2 text-[10px] text-amber-200/80" data-testid="ai-chat-news-sources">
              <summary className="cursor-pointer text-amber-300/90 hover:text-amber-200">News feed used for this brief ({newsSources.length} headlines, newest first — publish time ET)</summary>
              <ul className="mt-1 space-y-0.5">
                {newsSources.map((n, i) => (
                  <li key={i} className="flex gap-1.5" data-testid={`ai-chat-news-source-${i}`}>
                    <span className="font-mono text-amber-400/90 shrink-0 whitespace-nowrap">{n.published_et} · {n.age}</span>
                    <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline decoration-cyan-500/40 truncate"
                       onClick={e => { e.stopPropagation(); e.preventDefault(); window.open(n.url, "_blank", "noopener,noreferrer"); }}>
                      {n.source}: {n.title}
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {briefMeta && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[9px] text-amber-400/80" data-testid="ai-chat-brief-meta">
              {briefMeta.generated_at && <span>generated {new Date(briefMeta.generated_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}{briefMeta.cached ? ` · served from cache (${Math.round(briefMeta.cache_age || 0)}m old, no new tokens)` : ""}</span>}
              {briefMeta.usage && <UsageChip usage={briefMeta.usage} cached={briefMeta.cached} testId="ai-chat-brief-usage" />}
            </div>
          )}
        </div>
      )}

      {/* Evidence-based positioning idea (Overview page) */}
      {actionSuggestion && (
        <div className="border border-emerald-900/60 bg-emerald-950/20 rounded-md px-3 py-2 text-xs leading-relaxed text-emerald-100"
             data-testid="ai-chat-action-suggestion">
          <span className="text-[9px] uppercase tracking-wider text-emerald-400 font-bold mr-2">Suggested Action</span>
          {renderWithLinks(actionSuggestion)}
          <div className="mt-1 text-[9px] text-emerald-500/70">Evidence-informed analysis — not personalized financial advice</div>
        </div>
      )}

      {/* Data-aware LLM-generated questions */}
      {suggestions.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {suggestions.map((q, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPick(q)}
              disabled={sending}
              data-testid={`ai-chat-suggest-${i}`}
              className="text-left text-sm text-cyan-200 hover:text-cyan-100 hover:bg-slate-800/60 border border-slate-700 rounded px-3 py-2 transition-colors disabled:opacity-50"
            >
              &ldquo;{q}&rdquo;
            </button>
          ))}
        </div>
      )}

      {suggErr && !suggBusy && suggestions.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-rose-300 mt-1">
          <span>{suggErr}</span>
          <button
            type="button"
            onClick={onRetry}
            data-testid="ai-chat-suggest-retry"
            className="inline-flex items-center gap-1 rounded border border-rose-600 bg-rose-800/40 px-2 py-1 text-xs font-semibold text-rose-50 hover:bg-rose-700"
          >
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Token / cost chip for one LLM call ──────────────────────────────── */
function UsageChip({ usage, cached, testId }) {
  if (!usage) return null;
  return (
    <span className="inline-flex ml-2 items-center gap-1 rounded border border-amber-800/60 bg-amber-950/40 px-2 py-0.5 text-[10px] font-mono text-amber-200 align-middle"
          title={`${usage.model || ""} — ${usage.pricing || "estimate"}${cached ? " — this response came from cache; tokens shown were spent when it was first generated" : ""}`}
          data-testid={testId}>
      <Coins className="w-3 h-3 text-amber-400" />
      {usage.input_tokens.toLocaleString()} in + {usage.output_tokens.toLocaleString()} out = {usage.total_tokens.toLocaleString()} tok · ≈${Number(usage.est_cost_usd || 0).toFixed(4)}
    </span>
  );
}

/* ── Live elapsed-time indicator while waiting for the LLM ───────────── */
function ThinkingIndicator() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - t0) / 1000), 200);
    return () => clearInterval(id);
  }, []);
  const label = elapsed < 5  ? "thinking"
              : elapsed < 15 ? "reasoning through the data"
              : elapsed < 30 ? "researching citations"
              :                "still working — Claude is producing structured output";
  return (
    <div className="flex items-center gap-2 text-sm text-slate-400"
         data-testid="ai-chat-thinking">
      <RefreshCw className="w-4 h-4 animate-spin" />
      <span>{label}…</span>
      <span className="font-mono text-xs opacity-70">{elapsed.toFixed(1)}s</span>
    </div>
  );
}

/* Render markdown links [text](url) as clickable anchors; leave rest as text.
   Mobile-safe: touch-action manipulation, explicit window.open handler so taps
   work inside scrollable containers / in-app browsers. */
export function renderWithLinks(text) {
  if (!text) return text;
  if (!text.includes("](") && !text.includes("**")) return text;
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*/g;
  const parts = [];
  let last = 0, match;
  while ((match = re.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[3] !== undefined) {
      parts.push(<strong key={match.index} className="font-bold">{renderWithLinks(match[3])}</strong>);
      last = re.lastIndex;
      continue;
    }
    const url = match[2];
    parts.push(
      <a key={match.index} href={url} target="_blank" rel="noopener noreferrer"
         className="text-cyan-300 underline decoration-cyan-500/50 underline-offset-2 hover:text-cyan-200 cursor-pointer break-words px-0.5 py-1 -my-1 relative z-10"
         style={{ touchAction: "manipulation", WebkitTapHighlightColor: "rgba(34,211,238,0.35)" }}
         data-testid="ai-citation-link"
         onClick={(e) => {
           e.stopPropagation();
           e.preventDefault();
           window.open(url, "_blank", "noopener,noreferrer");
         }}>
        {match[1]}
      </a>
    );
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/* ── One rendered chat turn ───────────────────────────────────────────── */
function ChatMessage({ m }) {
  // Typewriter reveal — hooks must be at the top before any early return,
  // per React rules-of-hooks.
  const fullAnswer = m.answer || m.text || "";
  const [visible, setVisible] = useState(m.streaming ? "" : fullAnswer);
  const [streamingDone, setStreamingDone] = useState(!m.streaming);
  useEffect(() => {
    if (!m.streaming) { setVisible(fullAnswer); setStreamingDone(true); return; }
    let i = 0;
    const total = fullAnswer.length;
    // ~350 chars/s.  chunk of ~7 chars per 20ms tick.
    const tick = 20;
    const step = Math.max(3, Math.ceil(total / 120));
    const id = setInterval(() => {
      i = Math.min(i + step, total);
      setVisible(fullAnswer.slice(0, i));
      if (i >= total) { clearInterval(id); setStreamingDone(true); }
    }, tick);
    return () => clearInterval(id);
  }, [fullAnswer, m.streaming]);

  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-lg bg-purple-800/40 border border-purple-800 px-4 py-2.5 text-base text-purple-50">
          {m.text}
        </div>
      </div>
    );
  }
  const recCls =
    m.recommendation === "BUY"  ? "text-emerald-300 border-emerald-700 bg-emerald-950/60" :
    m.recommendation === "SELL" ? "text-rose-300 border-rose-700 bg-rose-950/60" :
                                  "text-amber-300 border-amber-700 bg-amber-950/40";

  return (
    <div className="flex justify-start">
      <div className="max-w-[95%] rounded-lg bg-slate-950/60 border border-slate-700 px-4 py-3 space-y-3">
        {m.recommendation && streamingDone && (
          <span className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-sm font-mono font-bold ${recCls}`}>
            <Zap className="w-3.5 h-3.5" />{m.recommendation}
          </span>
        )}
        {m.ai_source && streamingDone && (
          <span className="inline-flex ml-2 items-center rounded border border-slate-700 bg-slate-900/60 px-2 py-0.5 text-[10px] text-slate-400 align-middle"
                data-testid="ai-source-label">
            AI: {m.ai_source}
          </span>
        )}
        {m.usage && streamingDone && <UsageChip usage={m.usage} testId="ai-message-usage" />}
        <div className="text-base text-slate-100 whitespace-pre-line leading-relaxed">
          {streamingDone ? renderWithLinks(visible) : visible}
          {!streamingDone && (
            <span className="inline-block w-1.5 h-4 bg-cyan-300 align-middle ml-0.5 animate-pulse" />
          )}
        </div>
        {m.reasoning && streamingDone && (
          <div className="text-sm text-slate-300 italic border-l-2 border-slate-600 pl-3 py-1">
            {m.reasoning}
          </div>
        )}
        {Array.isArray(m.citations) && m.citations.length > 0 && streamingDone && (
          <div className="text-sm space-y-1.5 pt-2 border-t border-slate-800">
            <div className="text-slate-400 uppercase tracking-wider text-xs font-semibold">Sources</div>
            {m.citations.map((c, i) => (
              <a key={i} href={c.url} target="_blank" rel="noopener noreferrer"
                 data-testid={`ai-citation-${i}`}
                 className="flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 hover:underline">
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">
                  <span className="uppercase text-xs opacity-70 mr-1.5">{c.source_type}</span>
                  {c.title}
                  {c.published && <span className="ml-1.5 font-mono text-[10px] text-amber-300/90" data-testid={`ai-citation-published-${i}`}>· {c.published}</span>}
                </span>
              </a>
            ))}
          </div>
        )}
        {Array.isArray(m.suggested_stocks) && m.suggested_stocks.length > 0 && streamingDone && (
          <div className="text-sm text-slate-300">
            <span className="opacity-70 mr-1.5">Suggested stocks:</span>
            {m.suggested_stocks.map((t, i) => (
              <span key={i} className="inline-block mr-1.5 rounded bg-slate-800 border border-slate-700 px-2 py-0.5 font-mono">{t}</span>
            ))}
          </div>
        )}
        {Array.isArray(m.alternative_etfs) && m.alternative_etfs.length > 0 && streamingDone && (
          <div className="text-sm text-slate-300 space-y-1">
            <div className="opacity-70 font-semibold">Alternative ETFs:</div>
            {m.alternative_etfs.map((a, i) => (
              <div key={i}>
                <span className="font-mono text-cyan-300 font-semibold">{a.ticker}</span>
                <span className="text-slate-400 ml-2">{a.reason}</span>
              </div>
            ))}
          </div>
        )}
        {Array.isArray(m.proposed_allocation) && m.proposed_allocation.length > 0 && streamingDone && (
          <AllocationBlock allocation={m.proposed_allocation} curve={m.curve} />
        )}
      </div>
    </div>
  );
}

function AllocationBlock({ allocation, curve }) {
  return (
    <div className="pt-2 border-t border-slate-800 space-y-3" data-testid="ai-allocation-block">
      <div className="text-xs uppercase tracking-wider text-slate-300 font-semibold flex items-center gap-1.5">
        <LineChartIcon className="w-3.5 h-3.5" /> Proposed allocation
      </div>
      <div className="flex flex-wrap gap-1.5">
        {allocation.map((a, i) => (
          <span key={i} className="rounded bg-slate-800 border border-slate-700 px-2.5 py-1 text-sm font-mono"
                title={a.reason}>
            <span className="text-cyan-300 font-semibold">{a.ticker}</span>{" "}
            <span className="text-purple-300 font-bold">{a.weight_pct}%</span>
          </span>
        ))}
      </div>
      {curve?.curves?.allocation?.length > 0 && (
        <>
          <div className="text-sm text-slate-300 flex flex-wrap gap-x-4 gap-y-1">
            <span>Portfolio 1Y: <span className={`font-bold ${curve.stats.total_return_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
              {curve.stats.total_return_pct >= 0 ? "+" : ""}{curve.stats.total_return_pct}%
            </span></span>
            <span>SPY 1Y: <span className={`font-bold ${curve.stats.spy_return_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
              {curve.stats.spy_return_pct >= 0 ? "+" : ""}{curve.stats.spy_return_pct}%
            </span></span>
            <span>α: <span className={`font-bold ${curve.stats.alpha_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
              {curve.stats.alpha_pct >= 0 ? "+" : ""}{curve.stats.alpha_pct}%
            </span></span>
            <span>Sharpe: <span className="text-slate-100 font-bold">{curve.stats.sharpe}</span></span>
          </div>
          <div className="h-56 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={curve.curves.allocation.map((p, i) => ({
                date: p.date, alloc: p.value, spy: curve.curves.spy[i]?.value,
              }))}>
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#94a3b8" }} minTickGap={50} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} domain={["auto", "auto"]} width={50} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 13 }}
                         formatter={(v) => `$${Number(v).toFixed(2)}`} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 13 }} />
                <Line type="monotone" dataKey="alloc" stroke="#a78bfa" name="Allocation" dot={false} strokeWidth={2.5} />
                <Line type="monotone" dataKey="spy"   stroke="#38bdf8" name="SPY"        dot={false} strokeWidth={2.5} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
